"use client";

import { useContext, useState, useEffect } from "react";
import striptags from "striptags";
import { ResourceContext } from "@/contexts/resource-context";
import { logEvent } from "@/service/logs/log";
import { getAlphabeticalHash } from "@/service/text/headers";
import { scrollToPos } from "@/service/scroll/scroll";
import { cn } from "@/lib/utils";
import { ChevronDownIcon, ListIcon } from "lucide-react";
import { useDetectClickOutside } from "react-detect-click-outside";

export const TableOfContents = ({
  items = [],
  rightSidebarExists = true,
}: {
  items: TableOfContentsItem[];
  rightSidebarExists?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const { logView } = useContext(ResourceContext);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setIsFooterVisible(entry.isIntersecting);
      });
    });

    const footer = document.querySelector("#footer");
    if (footer) {
      observer.observe(footer);
    }

    return () => {
      if (footer) {
        observer.unobserve(footer);
      }
    };
  }, []);

  const internalRef = useDetectClickOutside({
    onTriggered: () => {
      setOpen(false);
    },
  });

  function clickHandler(item: TableOfContentsItem) {
    const header = document.querySelector(
      `#${getAlphabeticalHash(item.title)}[data-level="${item.level}"]`,
    );
    logEvent("click_table_of_contents", logView, {
      section: striptags(item.title),
    });

    if (!header) return;
    if (window.innerWidth < 1280) {
      header.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    const rmdv2Wrapper = document.querySelector(
      "#rmd-v2-wrapper",
    ) as HTMLElement;

    const top = header.getBoundingClientRect().top;

    scrollToPos({ left: 0, top: top + rmdv2Wrapper.scrollTop });
  }

  if (items.length < 2) {
    return <></>;
  }
  const wrapperStyles = cn(
    "transition-all fixed bottom-4 z-1000 left-4 rounded-xl bg-zinc-50 p-4 no-print",
    "xl:bottom-8 xl:left-[274px]",
    rightSidebarExists
      ? "min-[1800px]:translate-x-[-600px]! min-[1800px]:left-[50%]!"
      : "min-[1500px]:translate-x-[-450px]! min-[1500px]:left-[50%]!",
    "shadow-[0px_4px_24px_20px_rgba(255,255,255,0.50)] border border-zinc-200 backdrop-blur-2xl select-none ",
    isFooterVisible &&
      "transition-none! opacity-0 pointer-events-none translate-y-[48px]",
  );

  if (!open) {
    return (
      <div
        className={cn(wrapperStyles, "transition-all hover:cursor-pointer")}
        onClick={() => setOpen(!open)}
      >
        <div
          className={cn(
            `text-base-foreground flex items-center gap-2 text-sm font-bold`,
          )}
        >
          <ListIcon size={16} />
          Spis treści
        </div>
      </div>
    );
  }

  return (
    <div className={cn(wrapperStyles, `w-[320px]`)} ref={internalRef}>
      <div
        onClick={() => setOpen(!open)}
        className={
          "mb-4 flex items-center justify-between py-2 hover:cursor-pointer"
        }
      >
        <div
          className={cn(
            `text-base-foreground flex items-center gap-2 text-sm font-bold`,
          )}
        >
          <ListIcon size={16} />
          Spis treści
        </div>
        <ChevronDownIcon size={16} />
      </div>

      {/* Content */}
      <div className="toc-scrollbar max-h-80 overflow-x-hidden overflow-y-auto">
        {items.map((item, idx) => (
          <div key={idx} className="max-w-[calc(100%-16px)] py-1.5 first:pt-0">
            <div
              onClick={() => {
                clickHandler(item);
                setOpen(false);
              }}
              className={cn(
                "text-base-foreground hover:text-remedium-blue flex items-start justify-start gap-2 rounded text-sm font-medium transition-all hover:cursor-pointer",
                item.items.length === 0 && "ml-6 w-[calc(100%-24px)]",
              )}
            >
              {item.items.length > 0 && (
                <ChevronDownIcon
                  className={"min-w-4 translate-y-[3px]"}
                  size={16}
                />
              )}
              {striptags(item.title)}
            </div>

            {item.items.length > 0 && (
              <div className="mt-1.5 ml-6 flex flex-col gap-1.5">
                {item.items.map((item, itemIndex) => (
                  <div
                    key={itemIndex}
                    onClick={() => {
                      clickHandler(item);

                      setOpen(false);
                    }}
                    className="text-muted-foreground hover:text-remedium-blue w-full rounded text-sm font-medium transition-all hover:cursor-pointer"
                  >
                    {striptags(item.title)}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
